import { HttpError } from './HTTPErrors';

/**
 * `HttpError.name` values produced by `handleAxiosError` that represent transient
 * failures — i.e. errors where retrying is the correct recovery strategy.
 *
 * Keep this list aligned with the `name` values set in `categorizeAxiosError`.
 */
const TRANSIENT_ERROR_NAMES = new Set([
  'NetworkError', // Axios ERR_NETWORK → code 503
  'TimeoutError', // Axios ECONNABORTED / ETIMEDOUT → code 408
  'BadGateway', // HTTP 502
  'BadResponseError', // Axios ERR_BAD_RESPONSE → code 502
  'ServiceUnavailable', // HTTP 503
  'GatewayTimeout', // HTTP 504
  'client-closed-request', // HTTP 499 — client disconnected mid-request
]);

/**
 * HTTP status codes that always represent transient server-side failures,
 * used as a safety net when the error name cannot be determined.
 */
const TRANSIENT_STATUS_CODES = new Set([408, 499, 502, 503, 504]);

/**
 * String patterns matched against the lowercased error message when only a
 * serialised message string is available (e.g. stored in workflow context).
 * These intentionally mirror `TRANSIENT_ERROR_NAMES` so both code-paths agree.
 */
const TRANSIENT_PATTERNS = [
  // Axios ERR_NETWORK / strict network errors
  'network error',
  'networkerror',
  'unable to reach',
  'failed to fetch',
  // Mobile Safari (WebKit) fetch transport failure
  'load failed',
  'err_network',
  // HTTP 502 BadGateway / Axios ERR_BAD_RESPONSE
  'bad gateway',
  'server returned an invalid response',
  'err_bad_response',
  // Chrome, when a service worker script (sw.js) returns a 5xx or cannot be
  // fetched; the browser keeps the installed worker and retries next load
  'when fetching the script',
  // HTTP 503 ServiceUnavailable
  'temporarily unavailable',
  // HTTP 504 GatewayTimeout / Axios ECONNABORTED / ETIMEDOUT
  'gateway timeout',
  'request timeout',
  'econnaborted',
  'etimedout',
  // OS-level connectivity errors
  'econnrefused',
  'getaddrinfo',
  'enotfound',
  'eai_again',
  'err_name_not_resolved',
  'dns',
] as const;

/**
 * Returns `true` when the error represents a transient network or server failure
 * where retrying is the correct recovery strategy (502, 503, 504, ERR_NETWORK,
 * timeouts, etc.).
 *
 * Accepts a full error object (preferred — enables type-safe classification via
 * `HttpError` name/code) or any unknown value as a fallback.
 */
export function isTransientError(error: unknown): boolean {
  if (error instanceof HttpError) {
    if (TRANSIENT_ERROR_NAMES.has(error.name)) return true;
    // Safety net: classify by HTTP status code for known transient codes.
    // Catches edge cases where the error name could not be set correctly.
    if (
      typeof error.code === 'number' &&
      TRANSIENT_STATUS_CODES.has(error.code)
    )
      return true;
    return false;
  }

  // String-pattern fallback for serialised messages and unknown error shapes.
  let raw: string;
  if (error instanceof Error) {
    raw = error.message;
  } else if (error !== null && typeof error === 'object') {
    raw = JSON.stringify(error);
  } else {
    raw = String(error ?? '');
  }
  const msg = raw.toLowerCase();
  return TRANSIENT_PATTERNS.some((pattern) => msg.includes(pattern));
}
